import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Accordion,
  Badge,
  Spinner,
  Button,
} from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import PageLayout from "../../../layouts/PageLayout";

// readOnly: admin reporting (dashboard) view — no Edit / Give Loan actions
export default function ViewActGroup({ readOnly = false }) {
  const { id } = useParams();

  const backLink = readOnly
    ? "/act-groups-dashboard"
    : "/list-act-groups";

  const backText = readOnly
    ? "ACT Group Dashboard"
    : "ACT Groups";

  const AuthToken = localStorage.getItem("AuthUser");

  axios.defaults.headers.common[
    "Authorization"
  ] = `Bearer ${AuthToken}`;

  // ============================================================
  // STATES
  // ============================================================

  const [loading, setLoading] = useState(true);

  const [group, setGroup] = useState(null);

  const [members, setMembers] = useState([]);

  const [meetings, setMeetings] = useState([]);

  const [loans, setLoans] = useState([]);

  // ============================================================
  // LOAD GROUP
  // ============================================================

  useEffect(() => {
    fetchGroup();
  }, [id]);

  const fetchGroup = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/act-groups/${id}`
      );

      const data = response.data.data;

      setGroup(data);

      setMembers(data.members || []);

      setMeetings(data.meetings || []);

      setLoans(data.loans || []);
    } catch (error) {
      console.error(
        "Error loading ACT Group:",
        error
      );

      Swal.fire({
        title: "Error!",
        text:
          error.response?.data?.message ||
          "Failed to load ACT Group.",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // HELPERS
  // ============================================================

  const formatDate = (date) => {
    if (!date) {
      return "-";
    }

    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleDateString(
      "en-GB"
    );
  };

  const formatAmount = (amount) => {
    if (
      amount === null ||
      amount === undefined ||
      amount === ""
    ) {
      return "0";
    }

    return Number(amount).toLocaleString(
      "en-PK"
    );
  };

  const getGender = (gender) => {
    if (
      gender === 1 ||
      gender === "1"
    ) {
      return "Male";
    }

    if (
      gender === 2 ||
      gender === "2"
    ) {
      return "Female";
    }

    return gender || "-";
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case "leader":
        return "success";

      case "secretary":
        return "primary";

      case "member":
        return "secondary";

      default:
        return "secondary";
    }
  };

  const getMeetingImageUrl = (image) => {
    if (!image) {
      return "";
    }

    if (
      image.startsWith("http://") ||
      image.startsWith("https://")
    ) {
      return image;
    }

    const baseUrl =
      process.env.REACT_APP_IMG_URL || "";

    return `${baseUrl}${image}`;
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <PageLayout
        style={{ height: "100%" }}
      >
        <div className="text-center py-5">

          <Spinner
            animation="border"
            variant="success"
          />

          <p className="mt-3">
            Loading ACT Group...
          </p>

        </div>
      </PageLayout>
    );
  }

  // ============================================================
  // NOT FOUND
  // ============================================================

  if (!group) {
    return (
      <PageLayout
        style={{ height: "100%" }}
      >
        <div className="text-center py-5">

          <h4>
            ACT Group Not Found
          </h4>

          <Link to="/list-act-groups">

            <Button
              variant="secondary"
              className="mt-3"
            >
              Back to ACT Groups
            </Button>

          </Link>

        </div>
      </PageLayout>
    );
  }

  // ============================================================
  // SAVINGS & LOANS TOTALS
  // ============================================================

  const totalSavings = meetings.reduce(
    (sum, meeting) =>
      sum + Number(meeting.collected_amount || 0),
    0
  );

  const totalLoans = loans.reduce(
    (sum, loan) =>
      sum + Number(loan.amount || 0),
    0
  );

  // ============================================================
  // VIEW
  // ============================================================

  return (
    <PageLayout
      style={{ height: "100%" }}
    >

      {/* ======================================================
          BREADCRUMB
      ====================================================== */}

      <Row>
        <Col xl={12}>

          <div className="mc-card">

            <div className="mc-breadcrumb">

              <h3 className="mc-breadcrumb-title">

                <i className="material-icons">
                  groups
                </i>

                View ACT Group

              </h3>

              <ul className="mc-breadcrumb-list">

                <li className="mc-breadcrumb-item">

                  <Link
                    to="/"
                    className="mc-breadcrumb-link"
                    style={{
                      color: "#077834",
                    }}
                  >
                    Home
                  </Link>

                </li>

                <li className="mc-breadcrumb-item">

                  <Link
                    to={backLink}
                    className="mc-breadcrumb-link"
                    style={{
                      color: "#077834",
                    }}
                  >
                    {backText}
                  </Link>

                </li>

                <li className="mc-breadcrumb-item">
                  View
                </li>

              </ul>

            </div>

          </div>

        </Col>
      </Row>


      {/* ======================================================
          TOP ACTIONS
      ====================================================== */}

      <Row className="mb-3">

        <Col
          xl={12}
          className="d-flex justify-content-end gap-2"
        >

          <Link to={backLink}>

            <Button variant="secondary">
              Back
            </Button>

          </Link>


          {!readOnly && (

            <Link
              to={`/act-groups/edit/${group.id}`}
            >

              <Button variant="success">

                <i
                  className="material-icons"
                  style={{
                    fontSize: "17px",
                    verticalAlign: "middle",
                    marginRight: "5px",
                  }}
                >
                  edit
                </i>

                Edit Group

              </Button>

            </Link>

          )}


          {!readOnly && (

            <Link
              to={`/act-groups/loans/${group.id}`}
            >

              <Button variant="primary">

                <i
                  className="material-icons"
                  style={{
                    fontSize: "17px",
                    verticalAlign: "middle",
                    marginRight: "5px",
                  }}
                >
                  payments
                </i>

                Give Loan

              </Button>

            </Link>

          )}

        </Col>

      </Row>


      {/* ======================================================
          GROUP INFORMATION
      ====================================================== */}

      <Row>
        <Col xl={12}>

          <Card className="mb-4">

            <Card.Header>

              <div className="d-flex justify-content-between align-items-center">

                <h5 className="mb-0">
                  Group Information
                </h5>

                <Badge bg="success">
                  #{group.id}
                </Badge>

              </div>

            </Card.Header>


            <Card.Body>

              <Row>

                {/* Group Name */}

                <Col
                  md={6}
                  lg={4}
                  className="mb-4"
                >

                  <div className="text-muted mb-1">
                    Group Name
                  </div>

                  <strong>
                    {group.group_name || "-"}
                  </strong>

                </Col>


                {/* City */}

                <Col
                  md={6}
                  lg={4}
                  className="mb-4"
                >

                  <div className="text-muted mb-1">
                    City
                  </div>

                  <strong>
                    {group.city?.name ||
                      group.city_name ||
                      group.city_id ||
                      "-"}
                  </strong>

                </Col>


                {/* Gender */}

                <Col
                  md={6}
                  lg={4}
                  className="mb-4"
                >

                  <div className="text-muted mb-1">
                    Group Gender
                  </div>

                  <strong>
                    {getGender(
                      group.group_gender
                    )}
                  </strong>

                </Col>


                {/* Date */}

                <Col
                  md={6}
                  lg={4}
                  className="mb-4"
                >

                  <div className="text-muted mb-1">
                    Group Date
                  </div>

                  <strong>
                    {formatDate(group.date)}
                  </strong>

                </Col>


                {/* Agreed Amount */}

                <Col
                  md={6}
                  lg={4}
                  className="mb-4"
                >

                  <div className="text-muted mb-1">
                    Agreed Amount
                  </div>

                  <strong>
                    PKR{" "}
                    {formatAmount(
                      group.agreed_amount
                    )}
                  </strong>

                </Col>


                {/* Saving Frequency */}

                <Col
                  md={6}
                  lg={4}
                  className="mb-4"
                >

                  <div className="text-muted mb-1">
                    Saving Frequency
                  </div>

                  <strong>
                    {group.saving_frequency ||
                      "-"}
                  </strong>

                </Col>


                {/* Meeting Frequency */}

                <Col
                  md={6}
                  lg={4}
                  className="mb-4"
                >

                  <div className="text-muted mb-1">
                    Meeting Frequency
                  </div>

                  <strong>
                    {group.meeting_frequency ||
                      "-"}
                  </strong>

                </Col>


                {/* Mobilizers */}

                <Col
                  md={6}
                  lg={4}
                  className="mb-4"
                >

                  <div className="text-muted mb-1">
                    Mobilizers
                  </div>

                  <strong>
                    {Array.isArray(group.mobilizers)
                      ? group.mobilizers.join(", ") || "-"
                      : group.mobilizers || "-"}
                  </strong>

                </Col>


                {/* Admission */}

                <Col
                  md={6}
                  lg={4}
                  className="mb-4"
                >

                  <div className="text-muted mb-1">
                    Admission Form Status
                  </div>

                  <strong>
                    {group.admission_from_status === true ||
                    String(group.admission_from_status) === "1"
                      ? "Yes"
                      : "No"}
                  </strong>

                </Col>


                {/* Added By */}

                <Col
                  md={6}
                  lg={4}
                  className="mb-4"
                >

                  <div className="text-muted mb-1">
                    Added By
                  </div>

                  <strong>
                    {group.user?.name || "-"}
                  </strong>

                </Col>


                {/* Group Code */}

                <Col
                  md={6}
                  lg={4}
                  className="mb-4"
                >

                  <div className="text-muted mb-1">
                    Group Code
                  </div>

                  <strong>
                    {group.group_code || "-"}
                  </strong>

                </Col>


                {/* Address */}

                <Col
                  md={12}
                  className="mb-2"
                >

                  <div className="text-muted mb-1">
                    Address
                  </div>

                  <strong>
                    {group.address || "-"}
                  </strong>

                </Col>

              </Row>

            </Card.Body>

          </Card>

        </Col>
      </Row>


      {/* ======================================================
          DELIVERED ITEMS
      ====================================================== */}

      <Row>
        <Col xl={12}>

          <Card className="mb-4">

            <Card.Header>

              <h5 className="mb-0">
                Delivered Items
              </h5>

            </Card.Header>


            <Card.Body>

              <Row>

                <Col
                  md={6}
                  lg={3}
                  className="mb-3"
                >

                  <div className="d-flex justify-content-between align-items-center border rounded p-3">

                    <span>
                      Saving Box
                    </span>

                    {group.saving_box_delivered ? (

                      <Badge bg="success">
                        Delivered
                      </Badge>

                    ) : (

                      <Badge bg="secondary">
                        Not Delivered
                      </Badge>

                    )}

                  </div>

                </Col>


                <Col
                  md={6}
                  lg={3}
                  className="mb-3"
                >

                  <div className="d-flex justify-content-between align-items-center border rounded p-3">

                    <span>
                      Minute Book
                    </span>

                    {group.minute_book_delivered ? (

                      <Badge bg="success">
                        Delivered
                      </Badge>

                    ) : (

                      <Badge bg="secondary">
                        Not Delivered
                      </Badge>

                    )}

                  </div>

                </Col>


                <Col
                  md={6}
                  lg={3}
                  className="mb-3"
                >

                  <div className="d-flex justify-content-between align-items-center border rounded p-3">

                    <span>
                      Loan Ledger
                    </span>

                    {group.loan_ledger_delivered ? (

                      <Badge bg="success">
                        Delivered
                      </Badge>

                    ) : (

                      <Badge bg="secondary">
                        Not Delivered
                      </Badge>

                    )}

                  </div>

                </Col>


                <Col
                  md={6}
                  lg={3}
                  className="mb-3"
                >

                  <div className="d-flex justify-content-between align-items-center border rounded p-3">

                    <span>
                      Pass Book
                    </span>

                    {group.pass_book_delivered ? (

                      <Badge bg="success">
                        Delivered
                      </Badge>

                    ) : (

                      <Badge bg="secondary">
                        Not Delivered
                      </Badge>

                    )}

                  </div>

                </Col>

              </Row>

            </Card.Body>

          </Card>

        </Col>
      </Row>


      {/* ======================================================
          MEMBERS
      ====================================================== */}

      <Row>
        <Col xl={12}>

          <Card className="mb-4">

            <Card.Header>

              <div className="d-flex justify-content-between align-items-center">

                <h5 className="mb-0">
                  Group Members
                </h5>

                <Badge bg="success">
                  {members.length} Members
                </Badge>

              </div>

            </Card.Header>


            <Card.Body>

              <div className="table-responsive">

                <Table
                  bordered
                  hover
                  responsive
                  className="mb-0"
                >

                  <thead
                    style={{
                      backgroundColor:
                        "#077834",
                      color: "#fff",
                    }}
                  >

                    <tr>

                      <th>#</th>

                      <th>
                        Name
                      </th>

                      <th>
                        Role
                      </th>

                      <th>
                        CNIC
                      </th>

                      <th>
                        Mobile
                      </th>

                      <th>
                        Status
                      </th>

                    </tr>

                  </thead>


                  <tbody>

                    {members.length > 0 ? (

                      members.map(
                        (
                          member,
                          index
                        ) => (

                          <tr
                            key={
                              member.id
                            }
                          >

                            <td>
                              {index + 1}
                            </td>


                            <td>

                              <strong>
                                {member.name ||
                                  "-"}
                              </strong>

                            </td>


                            <td>

                              <Badge
                                bg={getRoleBadge(
                                  member.role
                                )}
                                style={{
                                  textTransform:
                                    "capitalize",
                                }}
                              >
                                {member.role ||
                                  "-"}
                              </Badge>

                            </td>


                            <td>
                              {member.cnic ||
                                "-"}
                            </td>


                            <td>
                              {member.mobile ||
                                "-"}
                            </td>


                            <td>

                              {member.status ? (

                                <Badge bg="success">
                                  Active
                                </Badge>

                              ) : (

                                <Badge bg="danger">
                                  Inactive
                                </Badge>

                              )}

                            </td>

                          </tr>

                        )
                      )

                    ) : (

                      <tr>

                        <td
                          colSpan="6"
                          className="text-center py-4"
                        >
                          No members found.
                        </td>

                      </tr>

                    )}

                  </tbody>

                </Table>

              </div>

            </Card.Body>

          </Card>

        </Col>
      </Row>


      {/* ======================================================
          MEETINGS
      ====================================================== */}

      <Row>
        <Col xl={12}>

          <Card className="mb-4">

            <Card.Header>

              <div className="d-flex justify-content-between align-items-center">

                <h5 className="mb-0">
                  Group Meetings
                </h5>

                <Badge bg="success">
                  {meetings.length} Meetings
                </Badge>

              </div>

            </Card.Header>


            <Card.Body>

              {meetings.length > 0 ? (

                <Accordion>

                  {meetings.map(
                    (
                      meeting,
                      index
                    ) => (

                      <Accordion.Item
                        eventKey={String(
                          index
                        )}
                        key={
                          meeting.id
                        }
                      >

                        <Accordion.Header>

                          <div
                            className="d-flex flex-wrap align-items-center gap-2"
                          >

                            <strong>
                              Meeting #
                              {index + 1}
                            </strong>

                            <span>
                              |
                            </span>

                            <span>
                              {formatDate(
                                meeting.date
                              )}
                            </span>

                            <span>
                              |
                            </span>

                            <Badge bg="success">

                              PKR{" "}
                              {formatAmount(
                                meeting.collected_amount
                              )}

                            </Badge>

                          </div>

                        </Accordion.Header>


                        <Accordion.Body>

                          {/* Meeting Summary */}

                          <Row>

                            <Col
                              md={6}
                              className="mb-4"
                            >

                              <div className="text-muted mb-1">
                                Meeting Date
                              </div>

                              <strong>
                                {formatDate(
                                  meeting.date
                                )}
                              </strong>

                            </Col>


                            <Col
                              md={6}
                              className="mb-4"
                            >

                              <div className="text-muted mb-1">
                                Collected Amount
                              </div>

                              <strong>
                                PKR{" "}
                                {formatAmount(
                                  meeting.collected_amount
                                )}
                              </strong>

                            </Col>


                            <Col
                              md={12}
                              className="mb-4"
                            >

                              <div className="text-muted mb-1">
                                Notes
                              </div>

                              <div
                                style={{
                                  whiteSpace:
                                    "pre-wrap",
                                }}
                              >
                                {meeting.notes ||
                                  "No notes available."}
                              </div>

                            </Col>

                          </Row>


                          {/* Images */}

                          <div>

                            <h6 className="mb-3">
                              Meeting Images
                            </h6>

                            {meeting.images &&
                            meeting.images.length >
                              0 ? (

                              <div className="d-flex flex-wrap gap-3">

                                {meeting.images.map(
                                  (
                                    image,
                                    imageIndex
                                  ) => (

                                    <a
                                      key={
                                        imageIndex
                                      }
                                      href={getMeetingImageUrl(
                                        image
                                      )}
                                      target="_blank"
                                      rel="noreferrer"
                                    >

                                      <img
                                        src={getMeetingImageUrl(
                                          image
                                        )}
                                        alt={`Meeting ${
                                          index +
                                          1
                                        }`}
                                        style={{
                                          width:
                                            "160px",
                                          height:
                                            "120px",
                                          objectFit:
                                            "cover",
                                          borderRadius:
                                            "8px",
                                          border:
                                            "1px solid #ddd",
                                          cursor:
                                            "pointer",
                                        }}
                                      />

                                    </a>

                                  )
                                )}

                              </div>

                            ) : (

                              <div className="text-muted">
                                No images uploaded for this meeting.
                              </div>

                            )}

                          </div>

                        </Accordion.Body>

                      </Accordion.Item>

                    )
                  )}

                </Accordion>

              ) : (

                <div className="text-center text-muted py-4">

                  No meetings found.

                </div>

              )}

            </Card.Body>

          </Card>

        </Col>
      </Row>


      {/* ======================================================
          SAVINGS & LOANS
      ====================================================== */}

      <Row>
        <Col xl={12}>

          <Card className="mb-4">

            <Card.Header>

              <div className="d-flex justify-content-between align-items-center">

                <h5 className="mb-0">
                  Savings & Loans
                </h5>

                {!readOnly && (

                  <Link
                    to={`/act-groups/loans/${group.id}`}
                  >

                    <Button
                      variant="primary"
                      size="sm"
                    >
                      Give Loan
                    </Button>

                  </Link>

                )}

              </div>

            </Card.Header>


            <Card.Body>

              <Row className="mb-3">

                <Col
                  md={4}
                  className="mb-3"
                >

                  <div className="text-muted mb-1">
                    Total Savings
                  </div>

                  <strong>
                    PKR{" "}
                    {formatAmount(
                      totalSavings
                    )}
                  </strong>

                </Col>


                <Col
                  md={4}
                  className="mb-3"
                >

                  <div className="text-muted mb-1">
                    Total Loans Given
                  </div>

                  <strong>
                    PKR{" "}
                    {formatAmount(
                      totalLoans
                    )}
                  </strong>

                </Col>


                <Col
                  md={4}
                  className="mb-3"
                >

                  <div className="text-muted mb-1">
                    Available Balance
                  </div>

                  <strong
                    className={
                      totalSavings - totalLoans < 0
                        ? "text-danger"
                        : ""
                    }
                  >
                    PKR{" "}
                    {formatAmount(
                      totalSavings - totalLoans
                    )}
                  </strong>

                </Col>

              </Row>


              <div className="table-responsive">

                <Table
                  bordered
                  hover
                  className="mb-0"
                >

                  <thead
                    style={{
                      backgroundColor:
                        "#077834",
                      color: "#fff",
                    }}
                  >

                    <tr>
                      <th>#</th>
                      <th>Member</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Time Period</th>
                      <th>Purpose</th>
                      <th>Evidence</th>
                    </tr>

                  </thead>


                  <tbody>

                    {loans.length > 0 ? (

                      loans.map(
                        (
                          loan,
                          index
                        ) => (

                          <tr key={loan.id}>

                            <td>
                              {index + 1}
                            </td>

                            <td>
                              <strong>
                                {loan.member?.name ||
                                  "-"}
                              </strong>
                            </td>

                            <td>
                              PKR{" "}
                              {formatAmount(
                                loan.amount
                              )}
                            </td>

                            <td>
                              {formatDate(
                                loan.loan_date
                              )}
                            </td>

                            <td>
                              {loan.time_period ||
                                "-"}
                            </td>

                            <td>
                              <Badge bg="primary">
                                {loan.purpose_text ||
                                  loan.purpose}
                              </Badge>
                            </td>

                            <td>

                              {loan.evidence ? (

                                <a
                                  href={getMeetingImageUrl(
                                    loan.evidence
                                  )}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  View
                                </a>

                              ) : (

                                "-"

                              )}

                            </td>

                          </tr>

                        )
                      )

                    ) : (

                      <tr>

                        <td
                          colSpan="7"
                          className="text-center py-4"
                        >
                          No loans given yet.
                        </td>

                      </tr>

                    )}

                  </tbody>

                </Table>

              </div>

            </Card.Body>

          </Card>

        </Col>
      </Row>


      {/* ======================================================
          BOTTOM ACTIONS
      ====================================================== */}

      <Row className="mb-5">

        <Col
          xl={12}
          className="d-flex gap-2"
        >

          <Link to={backLink}>

            <Button variant="secondary">
              Back to {backText}
            </Button>

          </Link>


          {!readOnly && (

            <Link
              to={`/act-groups/edit/${group.id}`}
            >

              <Button variant="success">

                <i
                  className="material-icons"
                  style={{
                    fontSize: "17px",
                    verticalAlign: "middle",
                    marginRight: "5px",
                  }}
                >
                  edit
                </i>

                Edit ACT Group

              </Button>

            </Link>

          )}

        </Col>

      </Row>

    </PageLayout>
  );
}
